import React, { useCallback, useMemo } from 'react';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import {SURFACE_BG} from '@/constants/colors'
import { useBushingStore } from '@/stores/useBushingStore';
import { useSampleStore } from '@/stores/useSampleStore';
import { PLOT_AXES_CONFIG } from '@/constants/plotAxes';
import { useTargetMarkers } from '@/hooks/useTargetMarkers';
import { useSampleSelection } from '@/hooks/useSampleSelection';

import ScatterChart from './ScatterChart';

/**
 * DualPlotPanel — Center column with two styled scatter plot cards.
 *
 * CHANGES:
 *  - Point clicks now go through useSampleSelection.selectSampleWithGeometry
 *    instead of the raw store setter, so switching to a sample whose
 *    geometry hasn't been generated yet for the current compute job
 *    drives the same loading overlay/progress bar as the initial Compute.
 */
const DualPlotPanel: React.FC = () => {
  const bushingType = useBushingStore((s) => s.bushingType);
  const displayLabels = useBushingStore((s) => s.displayLabels);

  const originalSamples = useSampleStore((s) => s.originalSamples);
  const generatedSamples = useSampleStore((s) => s.generatedSamples);

  const selectedId = useSampleStore((s) => s.currentSelectedId);
  const bestSampleId = useSampleStore((s) => s.bestSampleId);
  const { selectSampleWithGeometry } = useSampleSelection();

  const [target1, target2] = useTargetMarkers();

  const [axes1, axes2] = PLOT_AXES_CONFIG[bushingType];

  /** Look up the best sample row once; derive per-plot coordinates from it. */
  const bestRow = useMemo(
    () => (bestSampleId ? generatedSamples.find((r) => r.id === bestSampleId) ?? null : null),
    [bestSampleId, generatedSamples],
  );

  const bestX1 = bestRow ? (bestRow[axes1.xCol] as number | null) ?? null : null;
  const bestY1 = bestRow ? (bestRow[axes1.yCol] as number | null) ?? null : null;
  const bestX2 = bestRow ? (bestRow[axes2.xCol] as number | null) ?? null : null;
  const bestY2 = bestRow ? (bestRow[axes2.yCol] as number | null) ?? null : null;

  const handleSelect = useCallback(
    (id: string | null) => {
      void selectSampleWithGeometry(id);
    },
    [selectSampleWithGeometry],
  );

  const cardStyle = {
    flex: 1,
    display: 'flex',
    borderRadius: '18px',
    overflow: 'hidden',
    minHeight: 0,
    backgroundColor: SURFACE_BG,
    p: 1.5,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        height: '100%',
      }}
    >
      {/* Top Scatter Plot */}
      <Card sx={cardStyle}>
        <Box
          sx={{
            flex: 1,
            borderRadius: '14px',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <ScatterChart
            xCol={axes1.xCol}
            yCol={axes1.yCol}
            xLabel={displayLabels[axes1.xCol] ?? axes1.xCol}
            yLabel={displayLabels[axes1.yCol] ?? axes1.yCol}
            trainingData={originalSamples}
            generatedData={generatedSamples}
            selectedId={selectedId}
            targetX={target1.x}
            targetY={target1.y}
            bestX={bestX1}
            bestY={bestY1}
            bestId={bestSampleId}
            onSelect={handleSelect}
          />
        </Box>
      </Card>

      {/* Bottom Scatter Plot */}
      <Card sx={cardStyle}>
        <Box
          sx={{
            flex: 1,
            borderRadius: '14px',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <ScatterChart
            xCol={axes2.xCol}
            yCol={axes2.yCol}
            xLabel={displayLabels[axes2.xCol] ?? axes2.xCol}
            yLabel={displayLabels[axes2.yCol] ?? axes2.yCol}
            trainingData={originalSamples}
            generatedData={generatedSamples}
            selectedId={selectedId}
            targetX={target2.x}
            targetY={target2.y}
            bestX={bestX2}
            bestY={bestY2}
            bestId={bestSampleId}
            onSelect={handleSelect}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default DualPlotPanel;